import { cn } from "@productlane/lib/src/browser"
import { Loader } from "@productlane/ui"

import { api } from "@/lib/api"
import { FEATURE_ICONS } from "./feature-catalog"

const AGENT_FEATURE_NAME = "Productlane Agent"

export function FeatureAdoptionTable({
	onFeatureClick,
}: {
	onFeatureClick?: (featureName: string) => void
}) {
	const query = api.analytics.featureAdoption.useQuery({}, { staleTime: 30000 })

	if (!query.data || query.isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader />
			</div>
		)
	}

	const { totalPaying, features, agentChannels, periodDays } = query.data

	return (
		<div className="rounded-lg border border-gray-200 dark:border-gray-700/80">
			<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700/80">
				<h3 className="text-sm font-medium">Feature adoption</h3>
				<span className="text-sm text-gray-500 dark:text-gray-400">
					{totalPaying} paying customers
				</span>
			</div>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700/80 dark:text-gray-400">
						<th className="px-4 py-2 font-medium">Feature</th>
						<th className="px-4 py-2 text-right font-medium">
							Active paying customers
						</th>
						<th className="px-4 py-2 text-right font-medium">Share</th>
					</tr>
				</thead>
				<tbody>
					{features.map((feature) => {
						const share =
							totalPaying > 0
								? Math.round((feature.count / totalPaying) * 100)
								: 0
						const Icon = FEATURE_ICONS[feature.name]
						const isAgent = feature.name === AGENT_FEATURE_NAME
						return (
							<tr
								key={feature.name}
								className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50 dark:border-gray-700/80 dark:hover:bg-gray-800"
								onClick={
									onFeatureClick
										? () => onFeatureClick(feature.name)
										: undefined
								}
							>
								<td className="px-4 py-2 align-top">
									<span className="flex items-center gap-2">
										{Icon ? (
											<Icon className="h-4 w-4 flex-none text-gray-600 dark:text-gray-300" />
										) : null}
										{feature.name}
									</span>
									{isAgent && agentChannels.length > 0 ? (
										<span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 pl-6 text-xs text-gray-500 dark:text-gray-400">
											{agentChannels.map((channel) => (
												<span key={channel.name}>
													{channel.name}{" "}
													<span className="tabular-nums text-gray-700 dark:text-gray-300">
														{channel.count}
													</span>
												</span>
											))}
										</span>
									) : null}
								</td>
								<td className="px-4 py-2 text-right align-top tabular-nums">
									<span className="inline-flex items-baseline justify-end gap-2">
										{feature.count}
										<span
											title={`Change over the last ${periodDays} days`}
											className={cn(
												"inline-block min-w-[2rem] text-left text-xs",
												feature.change > 0 &&
													"text-green-600 dark:text-green-400",
												feature.change < 0 && "text-red-500",
											)}
										>
											{feature.change === 0
												? ""
												: `${feature.change > 0 ? "+" : ""}${feature.change}`}
										</span>
									</span>
								</td>
								<td className="px-4 py-2 text-right align-top tabular-nums text-gray-500 dark:text-gray-400">
									{share}%
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
